<template>
    <div>
      <SelectedEmailAndAssignment 
        :emailData="currentEmail"
        :assignment="selectedAssignment"
        :initiallyCollapsed="false"
        @assignmentChanged="onAssignmentChanged"/>

      <div class="date-field">
        <div class="current-due-date">
          <span class="current-due-date-text">Current Due Date: {{ currentDueDateDisplay }}</span>
        </div>
      </div>
      <div class="date-field">
        <DateComponent
        ref="dateComponent"
        :value="inputDate"
        label="Due Date"
        :required="true"
        field-id="dueDate"
        @input="onDateInput"
        />
      </div>
      </div>
      <div>
        <p style="font-size: x-small;">I attest the change and documentation is in <a href="#" @click.prevent="showComplianceModal = true" style="color:#006341;text-decoration:underline;cursor:pointer;">compliance</a> with the PAGA.”</p>
      </div>
       <div class="processing-options" v-if="config.processingOptions.markAsProcessed.visible">
      <p class="processing-options-title mt-2">Processing Options</p>
      <div class="checkbox-group">
        <v-checkbox v-model="markAsProcessed" :label="config.processingOptions.markAsProcessed.label" color="#003f2d"
          hide-details density="compact" class="custom-checkbox"></v-checkbox>
      </div>
    </div>
    <IsLoadingComponent v-if="showLoading" />
    <div class="comments-section" v-if="showComplianceModal">
      <div class="modal-overlay" @click.self="showComplianceModal = false">
        <div class="modal-content">
          <p>By updating the assignment due date, I attest to the following:</p>
          <ol class="pad-left">
            <li>The attached file represents a valid written agreement (email) from the client with explicit approval for a due date extension.</li>
            <li>The engagement originator and my local MD were copied on the attached client communication.</li>
            <li>I understand that these files are subject to internal audit to ensure proper documentation has been uploaded.</li>
          </ol>
          <button @click="showComplianceModal = false" style="margin-top:12px;padding:6px 18px;background:#006341;color:#fff;border:none;border-radius:4px;cursor:pointer;">Close</button>
        </div>
      </div>
    </div>
</template>

<script>
import { mapGetters ,mapActions, mapState } from 'vuex';
import BaseDialog from '../commons/BaseDialog.vue';
import DateComponent from '../commons/DateComponent.vue'; 
import SelectedAssignment from './assignment/SelectedAssignment.vue';
import IsLoadingComponent from '../IsLoadingComponent.vue'
import processingOptions from '../../mixins/processingOptions.js';
import { markEmailAsCompleteNew } from '../../service/Actions.js'; 
import IconProvider from '../commons/IconProvider.vue';
import { ACTION_TYPES } from '../../constants.js';
import { startTiming, addProcessingTime } from '../../utils/metricsHelper.js';
import SelectedEmailAndAssignment from '@/components/commons/SelectedEmailAndAssignment.vue';
export default {
  name: "ChangeDueDate",
  components: { 
    BaseDialog, 
    DateComponent,
    SelectedAssignment, 
    SelectedEmailAndAssignment,
    IsLoadingComponent,
    IconProvider
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    config: {
      type: Object,
      default: () => ({}),
    },
  },
  mixins: [processingOptions],
  data() {
    const today = new Date();
    // Format today's date as MM/DD/YYYY for the input field
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const year = today.getFullYear();
    const todayFormatted = `${month}/${day}/${year}`;
    
    return {
      year: today.getFullYear(),
      month: today.getMonth(),
      selectedDay: today.getDate(), // Set to current day as default
      markAsProcessed: true,
      today: today,
      newDueDate: today.toISOString().split('T')[0],
      showLoading: false,
      inputDate: todayFormatted, // Set today's date as default
      dateError: false, // For validation
      dateErrorMessage: '', // Error message to display
      updatedDueDate: null, // Local override for updated due date
      showComplianceModal: false, // Modal state for compliance info
    };
  },
  watch: {
    isValidForSubmit: {
      handler(newValue) {
        this.$emit('isValidForSubmit', newValue);
      },
      immediate: true,
    },
    // Watch for changes in selected assignment to update due date from backend
    selectedAssignment: {
      handler(newAssignment) {
        if (newAssignment && newAssignment.deadline && newAssignment.deadline.assignmentDueDate) {
          this.updatedDueDate = newAssignment.deadline.assignmentDueDate;
        } else {
          this.clearLocalOverrides();
        }
      },
      immediate: true
    },
    // Debug watcher for inputDate
    inputDate: {
      handler(newVal, oldVal) {
        console.log('ChangeDueDate: inputDate watcher triggered:', { oldVal, newVal });
        
        // Parse the date to update selectedDay, month, year for the updateDueDate function
        if (newVal && this.isValidDate(newVal)) {
          const [month, day, year] = newVal.split('/').map(Number);
          this.selectedDay = day;
          this.month = month - 1; // JavaScript months are 0-indexed
          this.year = year;
          console.log('ChangeDueDate: Date parsed to:', { day: this.selectedDay, month: this.month, year: this.year });
        }
      }
    },
    // Watch for dialog visibility to set default date
    visible: {
      handler(isVisible) {
        if (isVisible) {
          // Always set today's date when dialog opens
          this.setTodayDate();
        }
      }
    }
  },
  computed: {
     ...mapGetters({
      isLoading: 'assignments/isLoading', // Use the getter for assignments
      selectedAssignment: "assignments/selectedAssignment", // Use the getter for selected assignment
    }),
    ...mapState({
      currentEmail: (state) => state.email.currentEmail,
    }),
    currentDueDateDisplay() {
      let dueDateToDisplay = this.updatedDueDate;
      if (!dueDateToDisplay && this.selectedAssignment && 
          this.selectedAssignment.deadline && 
          this.selectedAssignment.deadline.assignmentDueDate) {
        dueDateToDisplay = this.selectedAssignment.deadline.assignmentDueDate;
      }
      if (!dueDateToDisplay) return '-';
      const date = new Date(dueDateToDisplay);
      // Format as MM/DD/YYYY in UTC
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      const year = date.getUTCFullYear();
      return `${month}/${day}/${year}`;
    },
    isButtonDisabled() {
      let result = !this.newDueDate || this.selectedAssignment === null; // Disable button if no date is selected or no assignment is selected
      return result;
    },
    isValidForSubmit() {
      return !this.isButtonDisabled;
    },
    formattedDate() {
      if (!this.selectedDay) return '';
      // Always format as UTC
      const date = new Date(Date.UTC(this.year, this.month, this.selectedDay));
      return date.toLocaleDateString('en-US', { 
        month: '2-digit', 
        day: '2-digit', 
        year: 'numeric',
        timeZone: 'UTC'
      });
    },
  },
  methods: {
     ...mapActions({ 
      convertToEmlFormData: "email/convertToEmlFormData",
      add:'notification/add',
      uploadFiles: "assignments/uploadFiles",
      updateAssignmentDueDate: "assignments/updateAssignmentDueDate",
      updateSelectedAssignmentDueDate: "assignments/updateSelectedAssignmentDueDate", // Add action to update selected assignment
      addMetricsData: "assignments/addMetricsData"
    }),
    // Handle date change from the DateComponent (not used with v-model, kept for compatibility)
    handleDateChange(newDate) {
      // This method is no longer needed with v-model, but kept for compatibility
    },
    onDateInput(val) {
      if (val && val.target && typeof val.target.value === 'string') {
        val = val.target.value;
      }
      if (typeof val !== 'string') return;

      this.inputDate = val;
      this.validateDate();
  },
    
    // Validation method (keeping the existing logic)
    isValidDate(dateString) {
      if (!dateString) return false;
      
      const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
      if (!regex.test(dateString)) return false;
      
      const [month, day, year] = dateString.split('/').map(Number);
      const date = new Date(year, month - 1, day);
      
      return date.getFullYear() === year &&
             date.getMonth() === month - 1 &&
             date.getDate() === day;
    },
    handleDateInput(newValue) {
      this.inputDate = newValue;
      this.dateError = false;
      
      // Force update the DateComponent display value directly
      this.$nextTick(() => {
        if (this.$refs.dateComponent) {
          this.$refs.dateComponent.displayValue = newValue;
        }
      });
    },
    
    // Method to set today's date consistently
    setTodayDate() {
      const today = new Date();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const year = today.getFullYear();
      const todayFormatted = `${month}/${day}/${year}`;
      
      this.handleDateInput(todayFormatted);
      return todayFormatted;
    },
    
    formatDateStringSimple(digits) {
      // Simple formatting without auto-padding
      if (digits.length === 0) {
        return '';
      } else if (digits.length <= 2) {
        return digits;
      } else if (digits.length <= 4) {
        return digits.slice(0, 2) + '/' + digits.slice(2);
      } else {
        const month = digits.slice(0, 2);
        const day = digits.slice(2, 4);
        const year = digits.slice(4, 8);
        
        // Don't auto-convert year during typing - keep it as user typed
        return month + '/' + day + '/' + year;
      }
    },
    
    formatWithSlashPadding(value) {
      // Parse the input that contains slashes
      const parts = value.split('/');
      let formatted = '';
      
      for (let i = 0; i < parts.length; i++) {
        if (i === 0) { // Month
          let month = parts[i];
          if (month.length === 1 && parts.length > 1) {
            month = '0' + month; // Pad single digit month when slash follows
          }
          formatted += month;
        } else if (i === 1) { // Day
          formatted += '/';
          let day = parts[i];
          if (day.length === 1 && parts.length > 2) {
            day = '0' + day; // Pad single digit day when slash follows
          }
          formatted += day;
        } else if (i === 2) { // Year
          formatted += '/';
          let year = parts[i];
          // Don't auto-convert year during typing - only expand when user finishes
          formatted += year;
        }
      }
      
      return formatted;
    },
    validateDate() {
      // Check if date is empty (mandatory field)
      if (!this.inputDate || this.inputDate.trim() === '') {
        this.dateError = true;
        this.dateErrorMessage = 'Due date is required';
        return false;
      }

      // The DateComponent already handles validation, so if we have a date, it's valid
      if (this.inputDate && this.isValidDate(this.inputDate)) {
        this.dateError = false;
        this.dateErrorMessage = '';
        return true;
      }
      
      this.dateError = true;
      this.dateErrorMessage = 'Please enter a valid date in MM/DD/YYYY format';
      return false;
    },
    onFocus() {
      this.isFocused = true;
      // Clear error message when user focuses on the field
      if (this.dateError && this.dateErrorMessage === 'Due date is required') {
        this.dateError = false;
        this.dateErrorMessage = '';
      }
    },
    closeDialog() {
      this.$emit('close');
    },
    async updateDueDate() {
      // Start timing
      const startTime = startTiming();
      
      // Initialize metrics payload
      const metricsPayload = {
        entityId: this.selectedAssignment.viewEventId,
        fileId: this.selectedAssignment.code,
        actionTaken: ACTION_TYPES.CHANGE_DUE_DATE,
        status: 'Completed'
      };

      let finalStatus = 'Completed';
      let errorMessage = null;

      try {
        console.log(this.inputDate)
        if (this.dateError) {
          return;
        }
        
        this.showLoading = true;

        const formData = await this.convertToEmlFormData();
        let multiFormData = new FormData();
        let document = formData.get('file');
        let documentName = document.name.replace('email_', 'ChangeDueDate_' + this.selectedAssignment.code + '_');
        let emlFile = new File([document], documentName, {
          type: document.type,
          lastModified: document.lastModified,
        });
        multiFormData.append('files', emlFile);
        multiFormData.append('folderName', 'Billing');

        // Check if workFileLink exists
        if (!this.selectedAssignment.workFileLink) {
          this.showLoading = false;
          let msg = {
            message: 'No work file link attached to this assignment',
            type: 'error',
          };
          this.$emit('showNotification', msg);
          finalStatus = 'Error';
          errorMessage = 'No work file link attached to this assignment';
          return;
        }

        let folderId = this.getWorkFileId(this.selectedAssignment.workFileLink);
        // Check if folderId was successfully extracted
        if (!folderId) {
          this.showLoading = false;
          this.add({
            message: 'Unable to extract folder ID from work file link. Please check the work file link format.',
            type: 'error',
            timeout:5
          });
          finalStatus = 'Error';
          errorMessage = 'Unable to extract folder ID from work file link';
          return;
        }
        let request = {
          folderId: folderId,
          multiPartFormData: multiFormData,
          config: {
            timeout: 0,
            maxContentLength: Infinity,
            maxBodyLength: Infinity,
          },
        };

        let boxResponse = await this.uploadFiles(request);
        const dateSelected = new Date(Date.UTC(this.year, this.month, this.selectedDay));
        const formatter = new Intl.DateTimeFormat('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
          timeZone: 'UTC'
        });
        const newDueDateSelected = formatter.format(dateSelected);

        if (boxResponse && boxResponse.length > 0) {
          let dueDateRequest = {
            assignmentDueDate: dateSelected.toISOString(), // Save as UTC ISO string
            entityIds: [this.selectedAssignment.viewEventId],
            fileId: this.selectedAssignment.code,
            documents: boxResponse,
            source: 'Outlook',
          };
          let response = await this.updateAssignmentDueDate(dueDateRequest);
          
          // Always update the local due date display after successful backend call
          this.updatedDueDate = dateSelected.toISOString();
          this.$forceUpdate();
          
          // Try to update the store as well
          if (this.updateSelectedAssignmentDueDate) {
            try {
              await this.updateSelectedAssignmentDueDate(newDueDateSelected);
            } catch (updateError) {
              console.warn('Failed to update via store action:', updateError);
            }
          }
          
          // Also try to update the assignment directly
          if (this.selectedAssignment && this.selectedAssignment.deadline) {
            this.selectedAssignment.deadline.assignmentDueDate = dateSelected.toISOString();
          }
          
          if(this.markAsProcessed) {
            // Mark email as complete with enhanced function - banner notification
            markEmailAsCompleteNew(
              'completeNotification',
              'Due Date updated successfully.',
              undefined,
              true
            );
          }

          this.showLoading = false;
          
          // Show success toaster notification in UI
          this.add({
            message: 'Due Date updated successfully.',
            type: 'success',
            timeout: 5
          });
          
          // Close dialog after a short delay to allow UI update
          setTimeout(() => {
            this.closeDialog();
          }, 100);
        }
      } catch (error) {
        console.error('Error saving due date:', error);
        this.showLoading = false;
        
        finalStatus = 'Error';
        errorMessage = error.message;
        
        let msg = {
          message: 'Failed to update due date.',
          type: 'error',
        };
        this.$emit('showNotification', msg);
      } finally {
        // Single metrics call with processing time
        const finalMetricsPayload = addProcessingTime(startTime, metricsPayload, finalStatus, errorMessage);
        await this.addMetricsData(finalMetricsPayload);
      }
    },
    getWorkFileId (value) {
      
      if (!value) {
        console.log('Debug - workFileLink is null/undefined');
        return null;
      }
      
      let arr;
      if (value.includes('?')) {
        let boxArr = value.split('?');
        arr = boxArr[0].split('/');
      } else {
        arr = value.split('/');
      }
      
      console.log('Debug - split array:', arr);
      const result = arr && arr.length > 0 ? arr[arr.length - 1] : null;
      console.log('Debug - extracted folderId:', result);
      
      return result;
    },
    closeCalendarOnOutsideClick(event) {
      if (!event.target.closest('.date-input-container')) {
        this.showCalendar = false;
      }
    },
    
    // Method to update the selected assignment's due date locally
    updateLocalAssignmentDueDate(newDueDateString) {
      if (this.selectedAssignment && this.selectedAssignment.deadline) {
        // Convert the formatted date string back to ISO format for storage using UTC
        const [month, day, year] = newDueDateString.split('/');
        const isoDate = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day))).toISOString();
        
        // Set local override first for immediate UI update
        this.updatedDueDate = isoDate;
        
        // Try store mutation first
        if (this.$store && this.$store.commit) {
          try {
            // Try to commit a mutation to update the store
            this.$store.commit('assignments/UPDATE_SELECTED_ASSIGNMENT_DUE_DATE', isoDate);
          } catch (mutationError) {
            console.log('Store mutation not available, using direct assignment');
            // If mutation doesn't exist, use direct update
            this.directUpdateAssignmentDueDate(isoDate);
          }
        } else {
          // Fallback to direct update
          this.directUpdateAssignmentDueDate(isoDate);
        }
      }
    },
    
    // Direct update method with proper reactivity
    directUpdateAssignmentDueDate(isoDate) {
      if (this.selectedAssignment && this.selectedAssignment.deadline) {
        // Use Vue.set to ensure reactivity
        if (this.$set) {
          this.$set(this.selectedAssignment.deadline, 'assignmentDueDate', isoDate);
        } else {
          // Fallback for Vue 3
          this.selectedAssignment.deadline.assignmentDueDate = isoDate;
        }
        
        // Force reactivity update
        this.$forceUpdate();
        
      }
    },
    
    // Clear local overrides to get fresh data
    clearLocalOverrides() {
      this.updatedDueDate = null;
    },
  },
  created() {
    // Ensure today's date is set as soon as the component is created
    this.setTodayDate();
    console.log('ChangeDueDate: created - setTodayDate called');
  },
  mounted() {
    // Always ensure today's date is set as default
    this.setTodayDate();
    console.log('ChangeDueDate: mounted - setTodayDate called');
    
    // Use nextTick to ensure the DateComponent is fully rendered
    this.$nextTick(() => {
      this.setTodayDate();
      console.log('ChangeDueDate: nextTick - setTodayDate called again');
    });
    
    // Clear any local overrides to ensure fresh data
    this.clearLocalOverrides();
    
    document.addEventListener('click', this.closeCalendarOnOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeCalendarOnOutsideClick);
  }
};
</script>
<style scoped>
.date-field {
  margin-bottom: 20px;
}

.current-due-date {
  margin-bottom: 16px;
}

.current-due-date-text {
  color: #333;
  font-size: 14px;
  font-weight: 500;
}

.due-date-label {
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-size: 14px;
  font-weight: normal;
}

.floating-input-container {
  position: relative;
  width: 100%;
  max-width: 400px;
}

.floating-label {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  font-size: 14px;
  pointer-events: none;
  transition: all 0.2s ease;
  background: white;
  padding: 0 4px;
}

.floating-label.float {
  top: 0;
  transform: translateY(-50%);
  font-size: 12px;
  color: #333;
}

.date-input-container {
  position: relative;
  margin: 20px 0;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 400px;
}

.date-input {
  width: 100%;
  padding: 6px 40px 8px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  background-color: #fff;
  height: 40px;
  color: #333;
  box-sizing: border-box;
}

.date-input:focus {
  border-color: #666;
  outline: none;
}

.date-input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.date-input:hover:not(:disabled) {
  border-color: #888;
}

.date-input.error {
  border-color: #dc3545;
}

.error-message {
  color: red;
  font-size: 0.675rem;
  margin-top: 0.25rem;
  display: block;
}

.calendar-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  cursor: pointer;
  width: 20px;
  height: 20px;
}

.calendar-popup {
  position: fixed;
  z-index: 2000;
  background: white;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  width: 90%;
  max-width: 300px;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  max-height: 80vh;
  overflow-y: auto;
}

/* Calendar component overrides */
:deep(.calendar-container) {
  border: none;
  padding: 0 16px;
}

:deep(.calendar-header) {
  margin: 12px 0;
  font-size: 14px;
  font-weight: 500;
  color: #003F2D;
}

:deep(.calendar-day.selected) {
  background-color: #003F2D;
  color: white;
}

:deep(.calendar-day) {
  padding: 8px;
  font-size: 13px;
}

:deep(.calendar-week) {
  padding: 8px;
  color: #666;
  font-size: 12px;
}

:deep(.calendar-day.today) {
  background-color: #E8F5E9;
  color: #003F2D;
  font-weight: 500;
}

:deep(.prev), :deep(.next-right) {
  color: #003F2D;
  border: none;
}

:deep(.submit-btn) {
  background-color: #003f2d;
  color: white;
  text-transform: none;
  font-size: 14px;
  padding: 0 24px;
}

:deep(.cancel-btn) {
  border-color: #003f2d;
  color: #003f2d;
  text-transform: none;
  font-size: 14px;
  margin-right: 12px;
}

.custom-checkbox {
  font-family: Calibre-R-Medium;
  font-style: Regular;
  vertical-align: middle;
  font-size: 14px;
}

.custom-checkbox :deep(label) {
  font-family: Calibre-R;
  font-size: 12px;
  margin-left: 8px;
}

.processing-options-title {
  font-family: Calibre-R;
  font-weight: 400;
  font-style: Regular;
  font-size: 14px;
  vertical-align: middle;
  color: #000;
}

.comments-section
 {
   font-family: Calibre-R;
   font-size: 10px;
 }
 .pad-left{
   padding-left: 16px;
 }
/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0,0,0,0.3);
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal-content {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.18);
  padding: 24px 20px 16px 20px;
  max-width: 400px;
  width: 90vw;
  font-size: 13px;
}
</style>
